import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../global-components/src";
import { useMessage } from "../../../global-components/src/index";
import { READBOOKS_API_URL } from "../../../global-components/src/utils/vars";
import { errorMessage } from "../../../global-components/src/utils/text";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import { CheckCircle, Clock, Copy, RefreshCw } from "react-feather";
import "./style.css";

const fmt = (v: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

interface Withdrawal {
    id: number;
    user: { id: number; name: string; email: string };
    amount: number;
    pix_key: string;
    pix_key_type: string;
    status: "pending" | "paid" | "failed";
    gateway_txid: string | null;
    created_at: string;
    processed_at: string | null;
}

export default function AdminWithdrawalsPage() {
    const { token } = useAuth();
    const { setMessage } = useMessage();

    const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
    const [loading, setLoading] = useState(true);
    const [confirmingId, setConfirmingId] = useState<number | null>(null);
    const [txidInput, setTxidInput] = useState<Record<number, string>>({});
    const [showConfirmFor, setShowConfirmFor] = useState<number | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${READBOOKS_API_URL}/admin/withdrawals`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWithdrawals(res.data.withdrawals);
        } catch (err: any) {
            setMessage(errorMessage(err), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const confirm = async (id: number) => {
        setConfirmingId(id);
        try {
            await axios.post(
                `${READBOOKS_API_URL}/admin/withdrawals/${id}/confirm`,
                { gateway_txid: txidInput[id] ?? null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessage("Saque confirmado!", "success");
            setShowConfirmFor(null);
            load();
        } catch (err: any) {
            setMessage(errorMessage(err), "error");
        } finally {
            setConfirmingId(null);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => setMessage("Copiado!", "success"));
    };

    const formatDate = (iso: string) =>
        new Date(iso).toLocaleString("pt-BR", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit"
        });

    const pixKeyTypeLabel: Record<string, string> = {
        cpf: "CPF", email: "E-mail", phone: "Telefone", random: "Aleatória"
    };

    return (
        <div className="admin-wd-wrapper margin-top-menu container">
            <div className="admin-wd-header">
                <div>
                    <h1>Saques pendentes</h1>
                    <p>{withdrawals.length} solicitação{withdrawals.length !== 1 ? "ões" : ""} aguardando</p>
                </div>
                <button className="btn btn-outline-secondary admin-wd-refresh" onClick={load}>
                    <RefreshCw size={15} /> Atualizar
                </button>
            </div>

            {loading ? (
                <div className="admin-wd-loading"><Spinner size="32px" /></div>
            ) : withdrawals.length === 0 ? (
                <div className="admin-wd-empty">
                    <CheckCircle size={40} />
                    <p>Nenhum saque pendente</p>
                </div>
            ) : (
                <div className="admin-wd-list">
                    {withdrawals.map(w => (
                        <div key={w.id} className="admin-wd-card">
                            {/* Top row */}
                            <div className="admin-wd-top">
                                <div className="admin-wd-user">
                                    <span className="admin-wd-name">{w.user.name}</span>
                                    <span className="admin-wd-email">{w.user.email}</span>
                                </div>
                                <span className="admin-wd-amount">{fmt(w.amount)}</span>
                            </div>

                            {/* PIX info */}
                            <div className="admin-wd-pix-row">
                                <div className="admin-wd-pix-type">
                                    <Clock size={13} />
                                    <span>{pixKeyTypeLabel[w.pix_key_type] ?? w.pix_key_type}</span>
                                </div>
                                <div className="admin-wd-pix-key">
                                    <code>{w.pix_key}</code>
                                    <button
                                        className="admin-wd-copy"
                                        onClick={() => copyToClipboard(w.pix_key)}
                                        title="Copiar chave PIX"
                                    >
                                        <Copy size={13} />
                                    </button>
                                </div>
                            </div>

                            <span className="admin-wd-date">Solicitado em {formatDate(w.created_at)}</span>

                            {/* Confirm inline */}
                            {showConfirmFor === w.id ? (
                                <div className="admin-wd-confirm-form">
                                    <input
                                        className="form-control form-control-sm"
                                        placeholder="ID do PIX enviado (opcional)"
                                        value={txidInput[w.id] ?? ""}
                                        onChange={e => setTxidInput(v => ({ ...v, [w.id]: e.target.value }))}
                                    />
                                    <div className="admin-wd-confirm-btns">
                                        <button
                                            className="btn btn-success btn-sm"
                                            disabled={confirmingId === w.id}
                                            onClick={() => confirm(w.id)}
                                        >
                                            {confirmingId === w.id ? <Spinner size="14px" /> : <><CheckCircle size={13} /> Confirmar</>}
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setShowConfirmFor(null)}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    className="btn btn-sm btn-outline-success admin-wd-action-btn"
                                    onClick={() => setShowConfirmFor(w.id)}
                                >
                                    <CheckCircle size={14} /> Marcar como pago
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}